import {
  useCallback,
  useMemo,
  type CSSProperties,
} from 'react'

import { Navigation } from '@react-native-common/common'
import cn from 'classnames'

import config from '@app/config'
import {
  navigationFeatureEnabled,
  switchToDashboardScreen,
  switchToHomeScreen,
} from '@app/helpers'
import {
  useAppDispatch,
  useAccess,
  useAzureClient,
  useCollectConversationMetrics,
  useConversationID,
  useCurrencies,
  useHubSpot,
  useNavigationModel,
  useRegionalSettings,
  useSyncStorage,
  useUser,
} from '@app/hooks'
import { logout } from '@app/services'
import { trackDownloadAppModalOpened } from '@app/services/tracking'
import { showModal } from '@app/store/modals'

import { AppHeader, Chat, Denied, List, AppSmartBanner } from './components'
import { DownloadPrompt } from './components/ConversationEmptyState/DownloadPrompt'
import { DOWNLOAD_APP_MODAL_KEY } from './components/ConversationEmptyState/constants'
import styles from './styles.module.scss'

export const AppRoute = () => {
  const dispatch = useAppDispatch()
  const { currencies } = useCurrencies()
  const { currentUser } = useUser()
  const conversationId = useConversationID()
  useHubSpot()
  const hasChatAccess = useAccess()
  useAzureClient()
  useCollectConversationMetrics()
  useSyncStorage()
  const isNavigationEnabled = navigationFeatureEnabled()
  const hasOpenedConversation = conversationId !== null
  const shouldShowNavigation =
    isNavigationEnabled &&
    !hasOpenedConversation
  const isB2C = !config.identity.isBackOffice
  const navigationVariant = isB2C ? 'b2c' : 'b2b'
  const shouldShowTabletBottomNavigation =
    isNavigationEnabled &&
    navigationVariant === 'b2c' &&
    !config.identity.isB2CWebView
  const navigationModel = useNavigationModel(navigationVariant)
  const {
    selectedCurrencyValue,
  } = useRegionalSettings({
    currencies,
    currentUser,
  })

  const containerStyle = {
    '--mobile-menu-height': shouldShowNavigation ? '50px' : '0px',
    '--tablet-menu-height': shouldShowTabletBottomNavigation ? '64px' : '0px',
    '--tablet-header-compensation': shouldShowTabletBottomNavigation
      ? 'var(--header-height)'
      : '0px',
    ...(config.identity.isB2CWebView ? { '--header-height': '0px' } : {}),
  } as CSSProperties
  const navigationModelWithCurrency = useMemo(() => {
    if (navigationVariant !== 'b2c' || !navigationModel.profile) {
      return navigationModel
    }

    return {
      ...navigationModel,
      profile: {
        ...navigationModel.profile,
        currency: selectedCurrencyValue,
      },
    }
  }, [navigationModel, navigationVariant, selectedCurrencyValue])
  const contentClassName = cn(styles.content, {
    [styles.withBottomNavigation]: shouldShowNavigation,
    [styles.withTabletBottomNavigation]: shouldShowTabletBottomNavigation,
  })
  const navigationClassName = cn(styles.navigation, {
    [styles.navigationEnabled]: isNavigationEnabled,
    [styles.hasOpenedConversation]: hasOpenedConversation,
  })

  const handleProfileAction = useCallback((item: { type: string }): boolean => {
    if (item.type === 'GetMobileApp') {
      trackDownloadAppModalOpened()
      dispatch(showModal(DOWNLOAD_APP_MODAL_KEY))
      return true
    }

    if (item.type === 'SwitchToHome') {
      void switchToHomeScreen()
      return true
    }

    if (item.type === 'SwitchToDashboard') {
      void switchToDashboardScreen()
      return true
    }

    if (item.type === 'Logout') {
      void logout()
      return true
    }

    return false
  }, [dispatch])

  return (
    <div className={styles.container} style={containerStyle}>
      {(!config.identity.isWebView ||
          window.location.href.includes('localhost')) && <AppSmartBanner />}
      {!config.identity.isB2CWebView && (
        <div className={navigationClassName}>
          <div className={styles.navigationWrapper}>
            <Navigation
              data={navigationModelWithCurrency}
              variant={navigationVariant}
              onProfileAction={handleProfileAction}
            />
          </div>
          <div className={styles.headerWrapper}>
            <AppHeader />
          </div>
        </div>
      )}
      <div className={contentClassName}>
        {hasChatAccess && (
          <>
            <List />
            <Chat />
          </>
        )}

        {!hasChatAccess && <Denied />}
      </div>

      <DownloadPrompt showPrompt={false} />
    </div>
  )
}
